import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import classes from "./EnsuringResults.module.css";
import { env } from "../../../constants/env";
import axios from "axios";
import CommonButton from "../../ui/CommonButton/CommonButton";

const EnsuringResults = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const userRole = user?.role;
  const [editable, setEditable] = useState(userRole === "ASSESSOR");

  const handleAnswerChange = (questionId, score, comment, points) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { score, comment, points },
    }));
  };

  useEffect(() => {
    if (!labId) return;
    fetchAccreditation();
  }, [labId, token, user._id]);

  const fetchAccreditation = async () => {
    setIsFetching(true);
    try {
      const res = await axios.get(`${env.kataApiUrl}/api/accreditation`, {
        params: {
          labId,
          section: "Ensuring Results",
          name: "SADCAS F134-B",
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success && res.data.accreditation) {
        const accreditation = res.data.accreditation;

        // Check if current user is the assigned auditor
        if (accreditation.auditorId !== user._id && userRole !== "ASSESSOR") {
          setEditable(false);
        }

        if (accreditation.answers && accreditation.answers.length > 0) {
          const mapped = accreditation.answers.reduce((acc, a) => {
            acc[a.questionId] = {
              score: a.score || "",
              comment: a.comment || "",
              points: a.points || 0,
            };
            return acc;
          }, {});
          setAnswers(mapped);
          setEditable(false); // answers exist → read-only
        } else {
          setEditable(true); // no answers → edit mode
        }
      }
    } catch (err) {
      console.error("Failed to fetch accreditation:", err);
    } finally {
      setIsFetching(false);
    }
  };

  const questionSets = [
    {
      questions1: [
        {
          questionId: "Q1",
          content:
            "a. Does the laboratory have a procedure for monitoring the validity of results? Is the monitoring planned and reviewed?",
          number: "7.3.7.1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q2",
          content:
            "b. Is data recorded in such a way that trends and shifts are detected and where practicable, statistical techniques applied to review the results?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      questions2: [
        {
          questionId: "Q3",
          content:
            "c. Does the laboratory have an IQC procedure for monitoring the ongoing validity of examination results?",
          number: "7.3.7.2 (a)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q4",
          content:
            "d. Does the procedure allow for detection of lot-to-lot reagent or calibrator variation?",
          number: "7.3.7.2 (a)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q5",
          content:
            "e. Is third-party IQC material considered as alternative or addition to manufacturer-supplied control material?",
          number: "7.2.3.2",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q6",
          content:
            "f. Does the laboratory select IQC material that is fit for its intended purpose? Consider stability, matrix, reactivity, clinical challenge?",
          number: "7.3.7.2(b)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q7",
          content:
            "g. Is IQC performed at a frequency based on method stability and patient risk?",
          number: "7.3.7.2(d)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q8",
          content:
            "h. Is IQC data reviewed with defined acceptability criteria at regular intervals?",
          number: "7.3.7.2(f)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q9",
          content:
            "i. Does the laboratory prevent release of patient results if IQC fails?",
          number: "7.3.7.2(g)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      questions3: [
        {
          questionId: "Comment1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      render: (onAnswerChange, answers) => (
        <>
          <QuestionSection
            topQuestion={false}
            accreditation
            addWidth
            showPointsField={false}
            questions={questionSets[0].questions1}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            // number="4.1"
            title="Internal Quality Control(IQC)"
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions2} // refer the array here
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            topQuestion={false}
            showQuestion={false}
            showEmptyNumber={false}
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions3}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
        </>
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  const handleDone = async () => {
    setIsSubmitting(true);
    try {
      const allQuestions = questionSets.flatMap((set) =>
        Object.keys(set)
          .filter((k) => k.startsWith("questions"))
          .flatMap((k) => set[k])
      );

      const answersArray = Object.entries(answers).map(([questionId, data]) => {
        const questionContent =
          allQuestions.find((q) => q.questionId === questionId)?.content || "";

        return {
          questionId,
          content: questionContent,
          score: data.score || undefined,
          comment: data.comment,
          points: data.points,
        };
      });

      const response = await fetch(`${env.kataApiUrl}/api/accreditation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: "SADCAS F134-B",
          labId: labId,
          section: "Ensuring Results",
          auditorId: user._id,
          answers: answersArray,
        }),
      });

      if (!response.ok) throw new Error("Submission failed");
      fetchAccreditation();
      toast.success("Updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to submit assessment");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">
            Clause 7.3.7: Ensuring the validity of examination results
          </h2>
        </div>
        {userRole === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditable(true)}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS B - Clause 7.3.7: Ensuring Results has been submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, answers)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={isSubmitting ? "Submitting..." : "Update"}
          disabled={isSubmitting}
        />
      )}
    </div>
  );
};

export default EnsuringResults;
